import { ComponentProps, CSSProperties, FC, useState } from "react";
import PhoneInput, { Country } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Text, { TextOverrideProps } from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import LockOutlineIcon from "@src/lib/icons/18px/lock-outline";

type Props = {
  value: string | undefined;
  label?: string;
  labelTextProps?: TextOverrideProps;
  onChange: (value: string | undefined) => void;
  className?: string;
  disabled?: boolean;
  style?: CSSProperties;
  error?: boolean;
  height?: "40px" | "32px";
  subtext?: string;
  required?: boolean;
  dataTestId?: string;
};

const PhoneNumberInput: FC<Props> = ({
  value,
  label = "Mobile Phone Number",
  labelTextProps,
  onChange,
  className,
  disabled,
  style,
  error,
  height = "32px",
  subtext,
  required,
  dataTestId,
}) => {
  const [countryCode, setCountryCode] = useState<Country | undefined>("US");
  return (
    <div className={cn("flex flex-col self-stretch", className)} style={style}>
      <div className="mb-1">
        {/* Legacy typography: callers override via TextOverrideProps (type/fontWeight). */}
        <Text
          type={"P3"}
          fontWeight={"SemiBold"}
          className="text-black"
          {...labelTextProps}
        >
          {label}
          {required && "*"}
        </Text>
      </div>
      <div className="relative ml-1">
        <PhoneInput
          data-testid={dataTestId}
          placeholder={countryCode === "US" && "(201) 555-5555"}
          defaultCountry="US"
          value={value}
          numberInputProps={{
            className: cn(
              "w-full outline-none shadow-sm rounded-[10px] text-sm font-normal box-border text-ellipsis font-inter",
              "placeholder:text-gray-8",
              height === "32px" ? "px-2" : "px-2.5",
              !error &&
                "border border-gray-2 focus:border-blue-3 disabled:border-gray-5-alpha disabled:bg-gray-4-alpha",
              error &&
                "border border-red-9 hover:border-red-9 focus:border-red-9",
              "text-black bg-white",
            ),
            style: {
              height,
            },
          }}
          onCountryChange={(country) => setCountryCode(country)}
          onChange={onChange}
          disabled={disabled}
          limitMaxLength
        />
        {disabled && (
          <LockOutlineIcon className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-10" />
        )}
      </div>
      {subtext && (
        <Text
          variant="fine-print-title"
          className={error ? "text-red-9" : "text-gray-7"}
          style={{ margin: "4px 0 0 0" }}
        >
          {subtext}
        </Text>
      )}
    </div>
  );
};

export default PhoneNumberInput;
